import { useQuery } from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import { ArrowLeft, GitCompare, Layers, Pin, Tags } from "lucide-react"
import {
	DetailPanel,
	DetailPanelSection,
	KeyValueItem,
	KeyValueList,
	PageHeader,
} from "@/components/layout"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { StatusDot, type StatusTone } from "@/components/ui/status"
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table"
import {
	FLUSH_TABLE_CLASS,
	SCANNER_DETAIL_PAGE_CLASS,
	ScannerDetailSplit,
} from "@/features/card-scanner/detail-blocks"
import { SuitePins } from "@/features/card-scanner/eval-shared"
import {
	Instant,
	MODEL_TYPE_LABELS,
	type ModelType,
	QueryError,
	useScannerGate,
} from "@/features/card-scanner/shared"
import { useMarketplaceOrpc } from "@/integrations/orpc/marketplace-react"
import { cn } from "@/utils/styles.helpers"

export const Route = createFileRoute("/card-scanner/eval-suites_/$suiteId")({
	component: EvalSuiteDetailPage,
})

const DIFF_TONES: Record<string, StatusTone> = {
	unchanged: "neutral",
	"re-pinned": "warn",
	added: "ok",
	removed: "bad",
}

function EvalSuiteDetailPage() {
	const allowed = useScannerGate()
	const { suiteId } = Route.useParams()
	const { orpc, isAuthReady } = useMarketplaceOrpc()

	const { data, isLoading, isError, error } = useQuery({
		...orpc.cardScanner.getEvalSuite.queryOptions({ input: { suiteId } }),
		enabled: allowed && isAuthReady,
	})

	if (!allowed) return null

	if (isLoading) {
		return (
			<div className={SCANNER_DETAIL_PAGE_CLASS}>
				<Skeleton className="h-10 w-96" />
				<Skeleton className="h-64 w-full" />
			</div>
		)
	}
	if (isError || !data) {
		return (
			<div className={SCANNER_DETAIL_PAGE_CLASS}>
				<BackLink />
				<QueryError
					what="the eval suite"
					error={error ?? new Error("Suite not found")}
				/>
			</div>
		)
	}

	const { suite, sets, previous, diff } = data
	const verified = sets.filter((set) => set.fingerprint).length

	return (
		<div className={SCANNER_DETAIL_PAGE_CLASS}>
			<div className="flex flex-col gap-3">
				<BackLink />
				<PageHeader
					title={
						<span className="inline-flex items-center gap-3">
							Suite v{suite.version}
							{suite.current && (
								<StatusDot tone="ok" className="font-normal text-sm">
									current
								</StatusDot>
							)}
						</span>
					}
					description={suite.note ?? undefined}
				/>
			</div>

			<ScannerDetailSplit
				aside={
					<DetailPanel title="Suite" icon={Pin}>
						<DetailPanelSection>
							<KeyValueList>
								<KeyValueItem label="Pinned by">{suite.pinnedBy}</KeyValueItem>
								<KeyValueItem label="Pinned">
									<Instant iso={suite.createdAt} />
								</KeyValueItem>
								<KeyValueItem label="Sets">{sets.length}</KeyValueItem>
								<KeyValueItem label="Verified">
									{verified}/{sets.length}
								</KeyValueItem>
							</KeyValueList>
						</DetailPanelSection>
					</DetailPanel>
				}
			>
				<DetailPanel title="Sets" icon={Layers} flush>
					<Table className={FLUSH_TABLE_CLASS}>
						<TableHeader>
							<TableRow>
								<TableHead>Set</TableHead>
								<TableHead>Task</TableHead>
								<TableHead>Measures</TableHead>
								<TableHead>Pins</TableHead>
								<TableHead className="text-right">Items</TableHead>
								<TableHead>Fingerprint</TableHead>
								<TableHead className="text-right">Games</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{sets.map((set) => (
								<TableRow key={set.id}>
									<TableCell className="font-mono text-xs">
										{set.name}
									</TableCell>
									<TableCell className="text-muted-foreground">
										{set.task}
										<span className="ml-1 text-[10px] uppercase">
											· {set.source}
										</span>
									</TableCell>
									<TableCell>
										{set.measures
											.map(
												(type) => MODEL_TYPE_LABELS[type as ModelType] ?? type,
											)
											.join(", ")}
									</TableCell>
									<TableCell>
										<SuitePins set={set} />
									</TableCell>
									<TableCell className="text-right tabular-nums">
										{set.itemCount ?? "—"}
									</TableCell>
									<TableCell className="font-mono text-xs">
										{set.fingerprint ? (
											<span title={set.fingerprint}>
												{set.fingerprint.slice(0, 12)}…
											</span>
										) : (
											<span className="font-sans text-meta">
												not yet verified
											</span>
										)}
									</TableCell>
									<TableCell className="text-right tabular-nums">
										{set.gamesCovered
											? Object.keys(set.gamesCovered).length
											: "—"}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</DetailPanel>

				<DetailPanel
					title={previous ? `Changes vs v${previous.suite.version}` : "Changes"}
					icon={GitCompare}
					flush
				>
					{previous ? (
						<Table className={FLUSH_TABLE_CLASS}>
							<TableHeader>
								<TableRow>
									<TableHead>Set</TableHead>
									<TableHead>Status</TableHead>
									<TableHead className="text-right">Items</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{diff.map((entry) => (
									<TableRow key={entry.name}>
										<TableCell className="font-mono text-xs">
											{entry.name}
										</TableCell>
										<TableCell>
											<StatusDot tone={DIFF_TONES[entry.status] ?? "neutral"}>
												{entry.status}
											</StatusDot>
										</TableCell>
										<TableCell className="text-right tabular-nums">
											{entry.itemsBefore ?? "—"} → {entry.itemsAfter ?? "—"}
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					) : (
						<DetailPanelSection>
							<p className="text-muted-foreground">
								First suite, nothing to compare against: every set in this suite
								is new.
							</p>
						</DetailPanelSection>
					)}
				</DetailPanel>

				<DetailPanel
					title="Labels snapshot"
					icon={Tags}
					meta="Registry games when pinned"
				>
					<DetailPanelSection>
						<div className="flex flex-wrap gap-1">
							{suite.labelsSnapshot.map((label) => (
								<Badge
									key={label.label}
									variant="neutral"
									className={cn(
										"font-mono font-normal",
										!label.enabled && "line-through opacity-60",
									)}
								>
									{label.labelIndex}·{label.label}
								</Badge>
							))}
						</div>
					</DetailPanelSection>
				</DetailPanel>
			</ScannerDetailSplit>
		</div>
	)
}

function BackLink() {
	return (
		<Link
			to="/card-scanner/eval-suites"
			className="inline-flex w-fit items-center gap-1 text-meta text-xs hover:text-foreground"
		>
			<ArrowLeft className="h-3.5 w-3.5" /> Eval suites
		</Link>
	)
}
